import React from "react";
import CustomButton from "../CustomButton";

/** 
 * Pagination component to handle page navigation.
 * @param {Object} props - Component properties.
 * @param {number} props.currentPage - The current page number.
 * @param {number} props.totalItems - Total number of items to paginate.
 * @param {function} props.onPageChange - Callback function to handle page change.
 * @param {number} [props.pageSize=5] - Number of items per page.
 * @returns {JSX.Element|null} - Rendered pagination component or null if not needed.
*/
const Pagination = ({
  currentPage,
  totalItems,
  onPageChange,
  pageSize = 5,
}) => {
  const totalPages = Math.ceil(totalItems / pageSize);
  if (totalPages <= 1) return null;

  let pages = [];
  const showPrevEllipsis = currentPage > 2;
  const showNextEllipsis = currentPage < totalPages - 1;

  if (currentPage === 1 && totalPages >= 3) {
    pages = [1, 2, 3];
    if (totalPages > 3) pages.push("...");
  } else if (currentPage === totalPages && totalPages >= 3) {
    if (totalPages > 3) pages.push("...");
    pages.push(totalPages - 2, totalPages - 1, totalPages);
  } else if (totalPages >= 3) {
    if (showPrevEllipsis) pages.push("...");
    pages.push(currentPage - 1, currentPage, currentPage + 1);
    if (showNextEllipsis) pages.push("...");
  } else {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  }

  return (
    <div className="d-flex justify-content-center gap-2 mt-3 align-items-center">
      <CustomButton
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        variant="outline"
      >
        Previous
      </CustomButton>
      {pages.map((page, idx) =>
        page === "..." ? (
          <span
            key={`ellipsis-${idx}`}
            style={{ minWidth: 36, textAlign: "center" }}
          >
            ...
          </span>
        ) : (
          <span
            key={page}
            onClick={() => onPageChange(page)}
            style={{
              cursor: "pointer",
              fontWeight: page === currentPage ? "bold" : "normal",
              minWidth: 36,
              textAlign: "center",
              padding: "0.25rem",
            }}
          >
            {page}
          </span>
        )
      )}
      <CustomButton
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        variant="outline"
      >
        Next
      </CustomButton>
    </div>
  );
};

export default Pagination;
